import classnames from 'classnames';

import { getHumanReadableBytes } from '../../../utils';
import Flex from '../Flex';

import styles from './styles.module.less';

type ProgressBarProps = {
  /**
   * Total amount of bytes for the thing being uploaded/downloaded
   */
  bytesTotal: number;
  /**
   * Total amount of bytes that have been uploaded/downloaded
   */
  bytesFinished: number;
  /**
   * type of Progress (Optional - @default 'UPLOAD')
   */
  progressType?: 'UPLOAD' | 'DOWNLOAD';
  /**
   * Has the upload/download failed/errored (Optional - @default false )
   */
  hasFailed?: boolean;
  /**
   * Error message to be displayed (Optional)
   */
  errorMessage?: string;
  /**
   * Custom message to be displayed (Optional)
   * If this is passed in, it will override the default message
   */
  customMessage?: string;
};

/**
 * A generic Progress bar component for uploading/downloading.
 * It will show the total bytes (in human readable format) as well as total percentage + a progress bar below
 * If @hasFailed is passed in as true, then the progress will halt and show in red, as well as show an optional
 * errorMessage under the ProgressBar
 */
const ProgressBar = ({ bytesFinished, bytesTotal, errorMessage, hasFailed = false, progressType = 'UPLOAD', customMessage }: ProgressBarProps) => {
  const percentFinished = 100 * (bytesFinished / bytesTotal);
  let message;
  if (hasFailed) {
    message = 'Error';
  } else if (bytesFinished === bytesTotal) {
    message = 'Complete';
  } else {
    message = progressType === 'UPLOAD' ? 'Uploading' : 'Downloading';
  }

  return (
    <div className={classnames({
      [styles.fossaProgressBar]: true,
      [styles.fossaProgressBarSuccess]: !hasFailed,
      [styles.fossaProgressBarError]: hasFailed,
    })}>
      <Flex justify="space-between">
        {!customMessage && <div className={styles.message}>{message}: {getHumanReadableBytes(bytesFinished)}</div>}
        {customMessage && <div className={styles.message}>{customMessage}</div>}
        <div className={styles.percentDisplay}>
          {Math.round(percentFinished)}%
        </div>
      </Flex>
      <div className={styles.fossaProgressBarOuter}>
        <div
          className={styles.fossaProgressBarInner}
          style={{
            width: `${Math.round(percentFinished)}%`,
          }}
        />
      </div>
      {errorMessage && <div className={styles.fossaProgressBarError}>{errorMessage}</div>}
    </div>
  );
};

export default ProgressBar;
